import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { GiftCardProvider, giftCardProvidersApi } from 'api/giftCardProviders'
import { StandaloneImagePickerValue } from 'components/ImagePicker/StandaloneImagePicker'
import { getUrlValue, UrlType, UrlValue } from 'utilities/content/linkUrls'
import { ApiImage } from 'utilities/types'
import { initialGiftCardsState } from './initialState'
import { GiftCardsState } from './types'

export type GiftCardState = {
  giftCards: GiftCardsState
}

const slice = createSlice({
  name: 'giftCards',
  initialState: initialGiftCardsState,
  reducers: {
    setSelectedProviderId: (state, action: PayloadAction<number>) => {
      state.selectedProviderId = action.payload
    },
    setCardImageType: (
      state,
      action: PayloadAction<{
        providerId: number
        cardImageType: GiftCardProvider['card_image_type']
      }>
    ) => {
      const { providerId, cardImageType } = action.payload
      if (!state.providers[providerId]) return
      state.providers[providerId] = {
        ...state.providers[providerId],
        cardImageType,
        hasChanges: true,
      }
    },
    setExternalLinkEnabled: (
      state,
      action: PayloadAction<{
        providerId: number
        externalLinkEnabled: boolean
      }>
    ) => {
      const { providerId, externalLinkEnabled } = action.payload
      if (!state.providers[providerId]) return
      state.providers[providerId] = {
        ...state.providers[providerId],
        externalLinkEnabled,
        hasChanges: true,
      }
    },
    setCardBackgroundColor: (
      state,
      action: PayloadAction<{ providerId: number; cardBackgroundColor: string }>
    ) => {
      const { providerId, cardBackgroundColor } = action.payload
      if (!state.providers[providerId]) return
      state.providers[providerId] = {
        ...state.providers[providerId],
        cardBackgroundColor,
        hasChanges: true,
      }
    },
    setExternalLinkUrl: (
      state,
      action: PayloadAction<{ providerId: number; externalLinkUrl: UrlValue }>
    ) => {
      const { providerId, externalLinkUrl } = action.payload
      if (!state.providers[providerId]) return
      state.providers[providerId] = {
        ...state.providers[providerId],
        externalLinkUrl: {
          ...externalLinkUrl,
          type: UrlType.External,
        },
        hasChanges: true,
      }
    },
    cardImageUploaded: (
      state,
      action: PayloadAction<{ providerId: number; fileUploadId: number }>
    ) => {
      const { providerId, fileUploadId } = action.payload
      if (!state.providers[providerId]) return
      state.providers[providerId] = {
        ...state.providers[providerId],
        cardImage: {
          ...state.providers[providerId].cardImage,
          fileUploadId,
          id: fileUploadId,
          uploading: false,
        },
        hasChanges: true,
      }
    },
    updateCardImage: (
      state,
      action: PayloadAction<{
        providerId: number
        updates: StandaloneImagePickerValue
      }>
    ) => {
      const { providerId, updates } = action.payload
      if (!state.providers[providerId]) return
      state.providers[providerId] = {
        ...state.providers[providerId],
        cardImage: {
          ...state.providers[providerId].cardImage,
          ...imageUpdates(updates),
        },
        hasChanges: true,
      }
    },
    removeCardImage: (state, action: PayloadAction<{ providerId: number }>) => {
      const { providerId } = action.payload
      if (!state.providers[providerId]) return
      state.providers[providerId] = {
        ...state.providers[providerId],
        cardImage: {
          ...getImageProperties(null),
          destroy: true,
        },
        hasChanges: true,
      }
    },
    linkImageUploaded: (
      state,
      action: PayloadAction<{ providerId: number; fileUploadId: number }>
    ) => {
      const { providerId, fileUploadId } = action.payload
      if (!state.providers[providerId]) return
      state.providers[providerId] = {
        ...state.providers[providerId],
        linkImage: {
          ...state.providers[providerId].linkImage,
          fileUploadId,
          id: fileUploadId,
          uploading: false,
        },
        hasChanges: true,
      }
    },
    updateLinkImage: (
      state,
      action: PayloadAction<{
        providerId: number
        updates: StandaloneImagePickerValue
      }>
    ) => {
      const { providerId, updates } = action.payload
      if (!state.providers[providerId]) return
      state.providers[providerId] = {
        ...state.providers[providerId],
        linkImage: {
          ...state.providers[providerId].linkImage,
          ...imageUpdates(updates),
        },
        hasChanges: true,
      }
    },
    removeLinkImage: (state, action: PayloadAction<{ providerId: number }>) => {
      const { providerId } = action.payload
      if (!state.providers[providerId]) return
      state.providers[providerId] = {
        ...state.providers[providerId],
        linkImage: {
          ...getImageProperties(null),
          destroy: true,
        },
        hasChanges: true,
      }
    },
  },
  extraReducers: builder => {
    builder.addMatcher(
      giftCardProvidersApi.endpoints.getGiftCardProviders.matchFulfilled,
      (state, action) => {
        const providers: GiftCardsState['providers'] = {}

        action.payload.gift_card_providers.forEach(provider => {
          providers[provider.id] = {
            id: provider.id,
            hasChanges: false,
            externalLinkEnabled: provider.external_link_enabled,
            cardImageType: provider.card_image_type,
            cardBackgroundColor: provider.card_background_color || '#2C3E50',
            externalLinkUrl: {
              ...getUrlValue(provider.external_link_url || ''),
              type: UrlType.External,
            },
            cardImage: getImageProperties(provider.card_background_image),
            linkImage: getImageProperties(provider.external_link_image),
          }
        })

        state.providers = providers
        state.selectedProviderId =
          action.payload.gift_card_providers[0]?.id ?? null
      }
    )
  },
})

const imageUpdates = (updates: StandaloneImagePickerValue) => ({
  id: updates.record_id ?? null,
  url: updates.url || '',
  fileUploadId: updates.id || undefined,
  uploading: updates.uploading,
  pendingImage: updates.pendingImage,
})

const getImageProperties = (image: ApiImage | null) => {
  if (!image) {
    return {
      id: null,
      url: '',
      uploading: false,
    }
  }

  return {
    id: image.id,
    url: image.default,
    uploading: false,
  }
}

export const reducer = slice.reducer
export const {
  setSelectedProviderId,
  setCardImageType,
  setExternalLinkEnabled,
  setCardBackgroundColor,
  setExternalLinkUrl,
  updateCardImage,
  removeCardImage,
  updateLinkImage,
  removeLinkImage,
  cardImageUploaded,
  linkImageUploaded,
} = slice.actions
export const selectGiftCardProvider = (
  state: GiftCardState,
  id: number | null
) => {
  if (!id) return null
  return state.giftCards.providers[id] ?? null
}
export const selectSelectedProviderId = (state: GiftCardState) =>
  state.giftCards.selectedProviderId
